import { useMutation } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { CustomerReportApiAdapter } from '../adapters';
import { CustomerExportResponse, CustomerFilters } from '../models';

export const useCustomerExport = () => {
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new CustomerReportApiAdapter(httpClient);

  const mutation = useMutation<CustomerExportResponse, Error, CustomerFilters | undefined>({
    mutationFn: (filters) => adapter.exportCustomerReport(filters),
    retry: (failureCount, error) => {
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const exportCustomers = (filters?: CustomerFilters) => {
    return mutation.mutateAsync(filters);
  };

  const downloadFromUrl = (url: string, filename = 'customer-report.xlsx') => {
    if (url.includes('example.com')) {
      console.log('🎭 Mock mode - Simulating download:', { url, filename });
      alert(`Mock mode: Simulating download of ${filename}\nURL: ${url}`);
      return;
    }
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async (filters?: CustomerFilters, filename?: string) => {
    const response = await exportCustomers(filters);
    downloadFromUrl(response.url, filename);
    return response;
  };

  const cancelExport = () => {
    mutation.reset();
  };

  return {
    exportCustomers,
    handleExport,
    downloadFromUrl,
    cancelExport,
    isExporting: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error?.message || null,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};